/** V2 Chat REST API client — typed wrappers for session CRUD and message loading. */

import type {
  CreateSessionRequest,
  CreateSessionResponse,
  ListSessionsParams,
  LoadMessagesParams,
  MessageListResponse,
  RawChatMessage,
  RawChatSession,
  RawCostInfo,
  RawDocumentRef,
  RawInstrumentRef,
  RawThoughtStep,
  RawUnitRef,
} from 'features/chatbot/types/api'
import type {
  AgentMode,
  ChatMessage,
  ChatSession,
  CostInfo,
  DocumentRef,
  InstrumentRef,
  MessageRole,
  MessageStatus,
  ThoughtStep,
  UnitRef,
} from 'features/chatbot/types/chat'

const V2_CHAT_PREFIX = '/api/v2/chat'

// ---------------------------------------------------------------------------
// Snake_case -> camelCase mappers
// ---------------------------------------------------------------------------

function mapDocumentRef(raw: RawDocumentRef): DocumentRef {
  return {
    id: raw.id,
    title: raw.title,
    drawingNumber: raw.drawing_number,
    documentType: raw.document_type,
    pageNumber: raw.page_number,
  }
}

function mapInstrumentRef(raw: RawInstrumentRef): InstrumentRef {
  return {
    id: raw.id,
    instrumentName: raw.instrument_name,
    piTag: raw.pi_tag,
    unitId: raw.unit_id,
    startDatetime: raw.start_datetime,
    endDatetime: raw.end_datetime,
  }
}

function mapUnitRef(raw: RawUnitRef): UnitRef {
  return {
    id: raw.id,
    name: raw.name,
    siteId: raw.site_id,
  }
}

function mapCostInfo(raw: RawCostInfo): CostInfo {
  return {
    inputTokens: raw.input_tokens,
    outputTokens: raw.output_tokens,
    thinkingTokens: raw.thinking_tokens,
    costUsd: raw.cost_usd,
  }
}

function mapThoughtStep(raw: RawThoughtStep): ThoughtStep {
  return {
    id: raw.id,
    seq: raw.seq,
    text: raw.text,
    toolCall: raw.tool_call,
    toolResultSummary: raw.tool_result_summary,
    references: raw.references
      ? {
          documents: raw.references.documents.map(mapDocumentRef),
          instruments: raw.references.instruments.map(mapInstrumentRef),
          units: raw.references.units.map(mapUnitRef),
        }
      : undefined,
  }
}

function mapMessage(raw: RawChatMessage): ChatMessage {
  return {
    id: raw.id,
    sessionId: raw.session_id,
    role: raw.role as MessageRole,
    sequence: raw.sequence,
    content: raw.content,
    status: raw.status as MessageStatus | undefined,
    streamId: raw.stream_id,
    persistedThroughSeq: raw.persisted_through_seq,
    thoughts: raw.thoughts.map(mapThoughtStep),
    allDocuments: raw.all_documents.map(mapDocumentRef),
    allInstruments: raw.all_instruments.map(mapInstrumentRef),
    allUnits: raw.all_units.map(mapUnitRef),
    isInitial: raw.is_initial,
    attachedDocumentIds: raw.attached_document_ids,
    modelName: raw.model_name,
    costInfo: raw.cost_info ? mapCostInfo(raw.cost_info) : undefined,
    thinkingTimeSeconds:
      raw.thinking_time_seconds === null ||
      raw.thinking_time_seconds === undefined
        ? undefined
        : raw.thinking_time_seconds,
    createdAt: raw.created_at,
  }
}

function mapSession(raw: RawChatSession): ChatSession {
  return {
    id: raw.id,
    orgId: raw.org_id,
    siteId: raw.site_id,
    userId: raw.user_id,
    mode: raw.mode as AgentMode,
    status: raw.status as ChatSession['status'],
    title: raw.title,
    modelName: raw.model_name,
    context: raw.context,
    linkedDocumentIds: raw.linked_document_ids,
    nextSequence: raw.next_sequence,
    messageCount: raw.message_count,
    totalCostUsd: raw.total_cost_usd,
    vertexSessionId: raw.vertex_session_id,
    createdAt: raw.created_at,
    updatedAt: raw.updated_at,
    preview: raw.preview ?? null,
    lastMessageStatus:
      (raw.last_message_status as MessageStatus | null | undefined) ?? null,
    activeStreamId: raw.active_stream_id ?? null,
    persistedThroughSeq: raw.persisted_through_seq ?? null,
  }
}

// ---------------------------------------------------------------------------
// Fetch helper
// ---------------------------------------------------------------------------

interface FetchOptions {
  method?: string
  body?: unknown
  signal?: AbortSignal
}

async function apiFetch<T>(
  baseUrl: string,
  path: string,
  authorization: string,
  options: FetchOptions = {}
): Promise<T> {
  const { method = 'GET', body, signal } = options
  const url = `${baseUrl}${V2_CHAT_PREFIX}${path}`

  const headers: Record<string, string> = {
    Authorization: authorization,
    'Content-Type': 'application/json',
  }

  const response = await fetch(url, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
    signal,
  })

  if (!response.ok) {
    const detail = await response.text().catch(() => response.statusText)
    throw new ChatApiError(response.status, detail)
  }

  return response.json() as Promise<T>
}

export class ChatApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly detail: string
  ) {
    super(`Chat API error ${status}: ${detail}`)
    this.name = 'ChatApiError'
  }
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export function createChatApi(baseUrl: string, authorization: string) {
  async function createSession(
    request: CreateSessionRequest
  ): Promise<CreateSessionResponse> {
    return apiFetch<CreateSessionResponse>(
      baseUrl,
      '/sessions',
      authorization,
      {
        method: 'POST',
        body: request,
      }
    )
  }

  async function listSessions(
    params: ListSessionsParams
  ): Promise<ChatSession[]> {
    const query = new URLSearchParams({
      org_id: params.orgId,
      site_id: params.siteId,
      user_id: params.userId,
    })
    if (params.limit !== undefined) {
      query.set('limit', String(params.limit))
    }
    const raw = await apiFetch<RawChatSession[]>(
      baseUrl,
      `/sessions?${query.toString()}`,
      authorization
    )
    return raw.map(mapSession)
  }

  async function getSession(sessionId: string): Promise<ChatSession> {
    const raw = await apiFetch<RawChatSession>(
      baseUrl,
      `/sessions/${sessionId}`,
      authorization
    )
    return mapSession(raw)
  }

  async function deleteSession(sessionId: string): Promise<{ status: string }> {
    return apiFetch<{ status: string }>(
      baseUrl,
      `/sessions/${sessionId}`,
      authorization,
      { method: 'DELETE' }
    )
  }

  async function loadMessages(
    params: LoadMessagesParams
  ): Promise<{ messages: ChatMessage[]; total: number; hasMore: boolean }> {
    const query = new URLSearchParams()
    if (params.offset !== undefined) {
      query.set('offset', String(params.offset))
    }
    if (params.limit !== undefined) {
      query.set('limit', String(params.limit))
    }
    const queryStr = query.toString()
    const path = `/sessions/${params.sessionId}/messages${queryStr ? `?${queryStr}` : ''}`
    const raw = await apiFetch<MessageListResponse>(
      baseUrl,
      path,
      authorization
    )
    return {
      messages: raw.messages.map(mapMessage),
      total: raw.total,
      hasMore: raw.has_more,
    }
  }

  function getSendMessageUrl(sessionId: string): string {
    return `${baseUrl}${V2_CHAT_PREFIX}/sessions/${sessionId}/messages`
  }

  function getReconnectUrl(streamId: string): string {
    return `${baseUrl}${V2_CHAT_PREFIX}/streams/${streamId}/reconnect`
  }

  return {
    createSession,
    listSessions,
    getSession,
    deleteSession,
    loadMessages,
    getSendMessageUrl,
    getReconnectUrl,
    authorization,
  }
}

export type ChatApi = ReturnType<typeof createChatApi>
